import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown, Bed, Bath, Users, Star, Phone, Mail, Instagram, Facebook,
  ArrowUpRight, Sparkles, Wine, Flame, Waves, Heart, Clock,
} from 'lucide-react';
import FloatingContact from '../../components/public/FloatingContact';
import ContactForm from '../../components/public/ContactForm';
import QuickBookWidget from '../../components/public/QuickBookWidget';
import { publicGet } from '../../lib/publicApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_GALLERY = [
  { url: 'https://images.unsplash.com/photo-1769643207226-dcc20cbe7d70?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85', alt: 'Suite signature' },
  { url: 'https://images.unsplash.com/photo-1731336479432-3eb5fdb3ab1c?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85', alt: 'Jacuzzi privé' },
  { url: 'https://images.unsplash.com/photo-1770625468069-9393585d7113?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85', alt: 'Sauna chaleureux' },
  { url: 'https://images.pexels.com/photos/13871319/pexels-photo-13871319.jpeg?auto=compress&cs=tinysrgb&w=2000', alt: 'Décor romantique' },
  { url: 'https://images.unsplash.com/photo-1775660922989-f0c624413269?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85', alt: 'Détail luxe' },
  { url: 'https://images.unsplash.com/photo-1703498201350-9ea3dcf9abd8?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85', alt: 'Champagne' },
];

const ICON_MAP = {
  jacuzzi: Waves,
  sauna: Flame,
  romantic: Heart,
  champagne: Wine,
  late: Clock,
  default: Sparkles,
};

const guessIcon = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('jacuzzi') || n.includes('spa')) return ICON_MAP.jacuzzi;
  if (n.includes('sauna')) return ICON_MAP.sauna;
  if (n.includes('roman') || n.includes('décor')) return ICON_MAP.romantic;
  if (n.includes('champ') || n.includes('vin')) return ICON_MAP.champagne;
  if (n.includes('tard') || n.includes('arriv')) return ICON_MAP.late;
  return ICON_MAP.default;
};

export default function LandingPage() {
  const [config, setConfig] = useState(null);
  const [property, setProperty] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  const [videoFailed, setVideoFailed] = useState(false);
  const heroRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    publicGet('/api/public/site-config').then(setConfig).catch(() => {});
    publicGet('/api/guest/properties').then(list => {
      if (Array.isArray(list) && list.length > 0) setProperty(list[0]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Robust autoplay: explicit play() with retry on visibility change & user interaction
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true; // mandatory for autoplay in all modern browsers
    v.setAttribute('playsinline', '');

    let cancelled = false;
    const tryPlay = () => {
      if (cancelled || !v) return;
      const p = v.play();
      if (p && p.catch) p.catch(() => { /* will retry on user interaction below */ });
    };

    tryPlay();

    // Retry when the page becomes visible again
    const onVisibility = () => { if (document.visibilityState === 'visible') tryPlay(); };
    document.addEventListener('visibilitychange', onVisibility);

    // First user interaction unlocks autoplay even if iOS blocked it
    const onUserGesture = () => { tryPlay(); };
    window.addEventListener('touchstart', onUserGesture, { once: true, passive: true });
    window.addEventListener('click', onUserGesture, { once: true });

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('touchstart', onUserGesture);
      window.removeEventListener('click', onUserGesture);
    };
  }, [property]);

  const brand = config?.brand_name || 'Era Lodge';
  const tagline = config?.tagline || 'Une parenthèse luxe & wellness';
  const heroVideo = property?.hero_video_url || config?.hero_video_url;
  const heroImage = property?.hero_image_url || config?.hero_image_url;
  const aboutText = property?.long_description || config?.about_text;

  const propertyMedia = property?.media || [];
  const propertyPhotos = propertyMedia.filter(m => m.type !== 'video');
  const propertyVideos = propertyMedia.filter(m => m.type === 'video');

  // Build gallery items: photos from property first, then fallback to fill
  const galleryPhotos = propertyPhotos.length >= 1
    ? [...propertyPhotos, ...FALLBACK_GALLERY].slice(0, 6)
    : FALLBACK_GALLERY;
  // Optional in-page autoplay video (different from hero)
  const showcaseVideo = propertyVideos[0];

  const optionItems = (property?.options && property.options.length > 0)
    ? property.options.filter(o => o.enabled !== false)
    : [
        { id: 'sauna', name: 'Sauna privé', description: 'Chaleur douce et bien-être', price: 40, image_url: 'https://images.unsplash.com/photo-1770625468069-9393585d7113?w=900&q=80' },
        { id: 'jacuzzi', name: 'Jacuzzi', description: 'Bulles et détente intime', price: 60, image_url: 'https://images.unsplash.com/photo-1731336479432-3eb5fdb3ab1c?w=900&q=80' },
        { id: 'romantic', name: 'Décoration romantique', description: 'Pétales, bougies, ambiance feutrée', price: 50, image_url: 'https://images.pexels.com/photos/13871319/pexels-photo-13871319.jpeg?w=900' },
        { id: 'late', name: 'Départ tardif', description: 'Rallongez votre instant jusqu\'à 14h', price: 30, image_url: 'https://images.unsplash.com/photo-1775660922989-f0c624413269?w=900&q=80' },
      ];

  const propertyId = property?.id;
  const bookHref = propertyId ? `/reserver/${propertyId}` : '#';

  const heroOpacity = Math.max(0, 1 - scrollY / 600);

  return (
    <div className="min-h-screen bg-[#0D0C0B] text-[#F5F2EB]" data-testid="landing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl md:text-3xl tracking-tight" data-testid="brand-link">
            <span className="text-[#F5F2EB]">Era</span>
            <span className="text-[#D4AF37] italic"> Lodge</span>
          </Link>
          <nav className="hidden md:flex items-center gap-10 text-xs uppercase tracking-[0.22em] text-[#A39E93] font-body">
            <a href="#suite" className="hover:text-[#D4AF37] transition-colors underline-gold" data-testid="nav-suite">La Suite</a>
            <a href="#galerie" className="hover:text-[#D4AF37] transition-colors underline-gold" data-testid="nav-gallery">Galerie</a>
            <a href="#options" className="hover:text-[#D4AF37] transition-colors underline-gold" data-testid="nav-options">Rituels</a>
            <a href="#contact" className="hover:text-[#D4AF37] transition-colors underline-gold" data-testid="nav-contact">Contact</a>
          </nav>
          <Link to={bookHref} className="hidden md:inline-flex items-center gap-2 px-6 py-2.5 border border-[#D4AF37] text-[#D4AF37] text-xs uppercase tracking-[0.22em] hover:bg-[#D4AF37] hover:text-[#0D0C0B] transition-colors" data-testid="header-book-btn">
            Réserver <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
          <Link to={bookHref} className="md:hidden px-4 py-2 border border-[#D4AF37] text-[#D4AF37] text-[10px] uppercase tracking-[0.22em]" data-testid="header-book-btn-mobile">
            Réserver
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section ref={heroRef} className="relative h-screen w-full overflow-hidden grain" data-testid="hero-section">
        {/* Always-visible image background as fallback (loaded immediately) */}
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt={brand}
            className={`w-full h-full object-cover ken-burns transition-opacity duration-700 ${videoFailed ? 'opacity-100' : 'opacity-100'}`}
          />
        </div>
        {/* Video layer on top — fades in when ready */}
        {heroVideo && !videoFailed && (
          <div className="absolute inset-0">
            <video
              ref={videoRef}
              autoPlay muted loop playsInline preload="auto"
              poster={heroImage}
              onError={() => setVideoFailed(true)}
              onCanPlay={(e) => { e.currentTarget.classList.add('opacity-100'); }}
              className="w-full h-full object-cover opacity-0 transition-opacity duration-700"
              data-testid="hero-video"
            >
              <source src={heroVideo} type="video/mp4" />
            </video>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/80" />

        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6" style={{ opacity: heroOpacity }}>
          <p className="overline mb-6 animate-fade-in stagger-1" data-testid="hero-overline">{tagline}</p>
          <h1 className="display-xl text-[clamp(2.5rem,7vw,6rem)] mb-6 animate-fade-up-slow stagger-2 max-w-5xl" data-testid="hero-title">
            <span className="text-[#F5F2EB]">L'art de la</span><br />
            <span className="italic text-[#D4AF37]">parenthèse</span>
          </h1>
          <p className="font-body font-light text-sm md:text-base text-[#A39E93] max-w-xl mb-6 leading-relaxed animate-fade-in stagger-3" data-testid="hero-subtitle">
            Une suite signature à Combs-la-Ville. Jacuzzi privé, sauna, rituels sur-mesure.
          </p>

          {/* Quick Book Widget — facile to reserve in 1 step */}
          <QuickBookWidget property={property} />

          <a href="#suite" className="mt-8 text-xs uppercase tracking-[0.22em] text-[#A39E93] hover:text-[#D4AF37] transition-colors font-body animate-fade-in stagger-5" data-testid="hero-discover-btn">
            ↓ Découvrir la suite
          </a>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 animate-scroll-hint" style={{ opacity: heroOpacity }}>
          <ChevronDown className="w-6 h-6 text-[#D4AF37]/70" strokeWidth={1.5} />
        </div>
      </section>

      {/* Sessions Bar */}
      <section className="relative bg-[#141312] border-y border-white/5" data-testid="sessions-bar">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/5">
          <div className="py-6 md:py-0 md:pr-12 flex items-center justify-between md:justify-start gap-8">
            <div>
              <p className="overline mb-2">Session Nuit</p>
              <p className="font-display text-3xl text-[#F5F2EB]">
                {property?.night_check_in_hour || '18:00'} <span className="text-[#A39E93] text-2xl">→</span> {property?.night_check_out_hour || '10:00'}
              </p>
              <p className="font-body text-sm text-[#A39E93] mt-1">À partir de €{property?.night_price ?? property?.base_price ?? 100} / nuit</p>
            </div>
          </div>
          <div className="py-6 md:py-0 md:pl-12 flex items-center justify-between md:justify-start gap-8">
            <div>
              <p className="overline mb-2">Day Use</p>
              <p className="font-display text-3xl text-[#F5F2EB]">
                {property?.dayuse_start_hour || '12:00'} <span className="text-[#A39E93] text-2xl">→</span> {property?.dayuse_end_hour || '15:00'}
              </p>
              <p className="font-body text-sm text-[#A39E93] mt-1">À partir de €{property?.dayuse_price ?? 120} / la session</p>
            </div>
          </div>
        </div>
      </section>

      {/* Suite intro */}
      <section id="suite" className="relative py-32 md:py-40 px-6 md:px-12" data-testid="suite-section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-20 items-center">
          <div className="md:col-span-5">
            <p className="overline mb-6">La Suite</p>
            <h2 className="font-display text-5xl md:text-6xl font-light leading-[0.95] mb-8 text-balance">
              Un cocon nocturne
              <span className="block italic text-[#D4AF37]">pour les sens.</span>
            </h2>
            <p className="font-body font-light text-base md:text-lg text-[#A39E93] leading-relaxed mb-10 max-w-md">
              {aboutText}
            </p>
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <Bed className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                <p className="text-sm text-[#A39E93] font-body uppercase tracking-[0.18em]">{property?.bedrooms ?? 1} chambre · lit king size</p>
              </div>
              <div className="flex items-center gap-4">
                <Bath className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                <p className="text-sm text-[#A39E93] font-body uppercase tracking-[0.18em]">Jacuzzi privé · Sauna</p>
              </div>
              <div className="flex items-center gap-4">
                <Users className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                <p className="text-sm text-[#A39E93] font-body uppercase tracking-[0.18em]">Jusqu'à {property?.max_guests ?? 2} personnes</p>
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={(galleryPhotos[0]?.url) || heroImage}
                alt="Suite"
                className="w-full h-full object-cover ken-burns"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      <div className="luxury-divider mx-auto max-w-7xl"></div>

      {/* Gallery */}
      <section id="galerie" className="relative py-32 md:py-40 px-6 md:px-12" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-16 gap-8">
            <div>
              <p className="overline mb-4">Galerie</p>
              <h2 className="font-display text-5xl md:text-6xl font-light leading-tight">L'instant en images</h2>
            </div>
            <p className="hidden md:block text-sm text-[#A39E93] font-body max-w-xs">
              Des matières profondes, une lumière feutrée, un univers à découvrir.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-12 gap-3 md:gap-5">
            <div className="col-span-2 md:col-span-7 aspect-[16/11] overflow-hidden group">
              <img src={galleryPhotos[0]?.url} alt={galleryPhotos[0]?.alt || 'Era Lodge'} className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
            </div>
            <div className="col-span-1 md:col-span-5 aspect-[4/5] overflow-hidden group">
              {showcaseVideo ? (
                <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                  <source src={showcaseVideo.url} type="video/mp4" />
                </video>
              ) : (
                <img src={galleryPhotos[1]?.url} alt={galleryPhotos[1]?.alt || 'Era Lodge'} className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
              )}
            </div>
            <div className="col-span-1 md:col-span-4 aspect-square overflow-hidden group">
              <img src={galleryPhotos[2]?.url} alt={galleryPhotos[2]?.alt || 'Era Lodge'} className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
            </div>
            <div className="col-span-1 md:col-span-4 aspect-square overflow-hidden group">
              <img src={galleryPhotos[3]?.url} alt={galleryPhotos[3]?.alt || 'Era Lodge'} className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
            </div>
            <div className="col-span-2 md:col-span-4 aspect-square overflow-hidden group">
              <img src={galleryPhotos[4]?.url || galleryPhotos[0]?.url} alt="Era Lodge" className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
            </div>
          </div>
        </div>
      </section>

      <div className="luxury-divider mx-auto max-w-7xl"></div>

      {/* Options / Rituels */}
      <section id="options" className="relative py-32 md:py-40 px-6 md:px-12 bg-[#141312]" data-testid="options-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <p className="overline mb-4">Rituels & Suppléments</p>
            <h2 className="font-display text-5xl md:text-6xl font-light leading-tight mb-6">
              Composez votre <span className="italic text-[#D4AF37]">expérience</span>
            </h2>
            <p className="font-body font-light text-base text-[#A39E93]">
              Chaque rituel se choisit en quelques clics au moment de la réservation.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
            {optionItems.slice(0, 8).map((opt, idx) => {
              const Icon = guessIcon(opt.name);
              return (
                <div
                  key={opt.id || idx}
                  className="group bg-[#0D0C0B] border border-white/5 hover:border-[#D4AF37]/40 transition-all duration-500"
                  data-testid={`option-card-${idx}`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[#1A1918]">
                    {opt.image_url ? (
                      <img src={opt.image_url} alt={opt.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon className="w-12 h-12 text-[#D4AF37]/40" strokeWidth={1} />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                      <p className="overline">+€{opt.price}</p>
                    </div>
                    <h3 className="font-display text-2xl text-[#F5F2EB] mb-2">{opt.name}</h3>
                    <p className="font-body font-light text-sm text-[#A39E93] leading-relaxed">{opt.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-20">
            <Link to={bookHref} className="btn-luxury" data-testid="options-book-btn">
              Configurer mon séjour
            </Link>
          </div>
        </div>
      </section>

      <div className="luxury-divider mx-auto max-w-7xl"></div>

      {/* Reviews */}
      <section className="py-32 md:py-40 px-6 md:px-12" data-testid="reviews-section">
        <div className="max-w-5xl mx-auto text-center">
          <p className="overline mb-6">Témoignages</p>
          <h2 className="font-display text-4xl md:text-5xl font-light leading-tight mb-16">Une parenthèse <span className="italic text-[#D4AF37]">inoubliable</span></h2>
          <div className="grid md:grid-cols-3 gap-10 md:gap-16">
            {[
              { name: 'Sophie & Antoine', text: 'Le sauna et le jacuzzi en privé, l\'ambiance feutrée — un anniversaire qu\'on n\'oubliera pas.' },
              { name: 'Léa M.', text: 'Le Day Use est une découverte. Trois heures suspendues, comme à l\'autre bout du monde.' },
              { name: 'Karim & Nadia', text: 'Décoration romantique impeccable. Tout était parfait, jusque dans le détail.' },
            ].map((r, i) => (
              <div key={i} className="text-center" data-testid={`review-${i}`}>
                <div className="flex items-center justify-center gap-1 mb-4">
                  {[...Array(5)].map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-[#D4AF37] text-[#D4AF37]" />)}
                </div>
                <p className="font-display text-xl italic text-[#F5F2EB] leading-relaxed mb-6">« {r.text} »</p>
                <p className="overline">{r.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT FORM Section */}
      <section className="py-32 md:py-40 px-6 md:px-12 bg-[#141312]" data-testid="contact-section">
        <div className="max-w-4xl mx-auto grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <p className="overline mb-4">Une question ?</p>
            <h2 className="font-display text-5xl md:text-6xl font-light leading-tight mb-6">
              Parlons <span className="italic text-[#D4AF37]">directement</span>
            </h2>
            <p className="font-body font-light text-[#A39E93] mb-10 leading-relaxed">
              Envie d'une attention particulière, d'organiser une surprise, ou simplement de vérifier les disponibilités ? Écrivez-nous, on vous répond très vite.
            </p>
            <div className="space-y-4 font-body text-sm">
              {config?.contact_email && (
                <a href={`mailto:${config.contact_email}`} className="flex items-center gap-3 text-[#A39E93] hover:text-[#D4AF37] transition-colors group" data-testid="contact-email-link">
                  <Mail className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                  <span className="underline-gold">{config.contact_email}</span>
                </a>
              )}
              {(config?.wero_phones || []).map((p) => (
                <a key={p} href={`tel:+33${p.replace(/^0/, '')}`} className="flex items-center gap-3 text-[#A39E93] hover:text-[#D4AF37] transition-colors group" data-testid={`contact-phone-${p}`}>
                  <Phone className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                  <span className="underline-gold tabular-nums">{p}</span>
                </a>
              ))}
              {config?.address && (
                <p className="flex items-center gap-3 text-[#A39E93]">
                  <Sparkles className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                  <span>{config.address}</span>
                </p>
              )}
            </div>
          </div>
          <div className="md:col-span-7">
            <ContactForm propertyId={propertyId} />
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="relative py-32 md:py-40 overflow-hidden" data-testid="cta-final">
        <div className="absolute inset-0">
          <img src={galleryPhotos[1]?.url || heroImage} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative z-10 text-center px-6">
          <p className="overline mb-6">Réservation directe</p>
          <h2 className="font-display text-5xl md:text-7xl font-light leading-[0.95] mb-8 max-w-3xl mx-auto text-balance">
            Offrez-vous l'instant.
          </h2>
          <p className="font-body font-light text-[#A39E93] max-w-md mx-auto mb-12">
            Disponibilité en temps réel. Confirmation immédiate. Paiement sécurisé.
          </p>
          <Link to={bookHref} className="btn-luxury" data-testid="cta-final-btn">
            Réserver ma suite
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#0A0908] border-t border-white/5 px-6 md:px-12 py-16">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-12">
          <div>
            <p className="font-display text-3xl mb-4">
              <span className="text-[#F5F2EB]">Era</span>
              <span className="text-[#D4AF37] italic"> Lodge</span>
            </p>
            <p className="font-body font-light text-sm text-[#A39E93] leading-relaxed max-w-xs">{tagline}</p>
          </div>
          <div>
            <p className="overline mb-6">Contact</p>
            <ul className="space-y-3 text-sm text-[#A39E93] font-body font-light">
              {config?.contact_email && (
                <li className="flex items-center gap-3"><Mail className="w-3.5 h-3.5 text-[#D4AF37]" /> {config.contact_email}</li>
              )}
              {config?.contact_phone && (
                <li className="flex items-center gap-3"><Phone className="w-3.5 h-3.5 text-[#D4AF37]" /> {config.contact_phone}</li>
              )}
              {config?.address && <li className="text-[#A39E93]/80">{config.address}</li>}
            </ul>
          </div>
          <div>
            <p className="overline mb-6">Suivez-nous</p>
            <div className="flex items-center gap-4">
              {config?.instagram_url && (
                <a href={config.instagram_url} target="_blank" rel="noreferrer" className="w-10 h-10 border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center justify-center transition-colors" data-testid="footer-instagram">
                  <Instagram className="w-4 h-4" />
                </a>
              )}
              {config?.facebook_url && (
                <a href={config.facebook_url} target="_blank" rel="noreferrer" className="w-10 h-10 border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center justify-center transition-colors" data-testid="footer-facebook">
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              <Link to={bookHref} className="ml-auto md:ml-0 text-xs uppercase tracking-[0.22em] text-[#D4AF37] hover:text-[#F0C84B]" data-testid="footer-book">
                Réserver →
              </Link>
            </div>
          </div>
        </div>
        <div className="luxury-divider my-10 max-w-7xl mx-auto" />
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#5C5851] font-body">
          <p>© {new Date().getFullYear()} {brand}. Tous droits réservés.</p>
          <Link to="/admin/login" className="hover:text-[#D4AF37] transition-colors uppercase tracking-[0.22em]" data-testid="footer-admin-link">
            Espace gestion
          </Link>
        </div>
      </footer>

      {/* Floating Contact (WhatsApp + Tel + Email) */}
      <FloatingContact />
    </div>
  );
}
